import { useEffect, useMemo, useRef, useState } from "react";
import { Map as MapIcon, Navigation } from "lucide-react";
import { coordsOf, formatDistance, haversineKm } from "@/lib/geo";
import { useTranslation } from "@/i18n/I18nProvider";
import { RestaurantCard } from "./RestaurantCard";
import { ArrowBtn, CARD_WRAP, TRACK } from "./VenueStrip";
import type { GeolocationState } from "@/hooks/useGeolocation";
import type { Discount, Restaurant, RestaurantId } from "@/types/restaurant";

export interface NearbyStripProps {
  /** Open-first ordering from the feed: each venue + whether it's currently open. */
  restaurants: { r: Restaurant; open: boolean }[];
  /** Active discounts keyed by venue — passed straight through to the card. */
  discountsByRestaurant: Record<RestaurantId, Discount[]>;
  geo: GeolocationState;
  /** Feed still loading — show skeletons instead of an empty strip. */
  loading: boolean;
  onSelect: (restaurant: Restaurant) => void;
  /** Open the live map (button hidden when not provided). */
  onOpenMap?: () => void;
}

/** Only surface venues this close to the visitor (km). Tunable. */
const NEARBY_RADIUS_KM = 30;
const NEARBY_MAX = 12;

/**
 * "Closest to you" — a horizontal, swipeable strip of the nearest OPEN venues,
 * ranked by real distance from the visitor's geolocation. Lives at the top of
 * the Recommended feed. While a result is still possible (feed loading, first
 * fix pending) it shows the section header + skeletons; once it's certain there
 * is nothing to rank (permission denied, geolocation unavailable, or nothing
 * open in range) the whole section collapses to a single slim row that says
 * why — and keeps the map reachable, because the live map works fine without
 * geolocation.
 */
export function NearbyStrip({ restaurants, discountsByRestaurant, geo, loading, onSelect, onOpenMap }: NearbyStripProps) {
  const { t, lang } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  const nearby = useMemo(() => {
    if (!geo.coords) return [];
    const here = geo.coords;
    return restaurants
      .filter(({ open }) => open)
      .map(({ r }) => {
        const c = coordsOf(r);
        return c ? { r, km: haversineKm(here, c) } : null;
      })
      .filter((x): x is { r: Restaurant; km: number } => x !== null && x.km <= NEARBY_RADIUS_KM)
      .sort((a, b) => a.km - b.km)
      .slice(0, NEARBY_MAX);
  }, [restaurants, geo.coords]);

  // Show the desktop arrows only when the track actually overflows, so cities
  // with a couple of nearby venues don't get dead controls.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const check = () => setOverflowing(el.scrollWidth - el.clientWidth > 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [nearby.length]);

  const scrollBy = (dir: 1 | -1) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 360), behavior: "smooth" });
  };

  // A result is still possible while the feed loads or the first fix is pending
  // (idle → auto-prompt → prompting read as "no coords yet"). Denied and
  // unavailable are terminal: skip the skeletons and collapse right away.
  const pending = (loading || !geo.coords) && geo.status !== "denied" && geo.status !== "unavailable";
  const hasResults = !pending && geo.status === "granted" && nearby.length > 0;

  // Map works with or without geolocation, so it isn't gated on results — only
  // on the feed having loaded (there's nothing to plot before that).
  const mapPill = onOpenMap && !loading && (
    <button
      type="button"
      onClick={onOpenMap}
      className="inline-flex h-12 items-center gap-2 rounded-full border border-white/20 light:border-black/15 bg-zinc-900/70 light:bg-white/70 px-5 text-[15px] font-medium text-zinc-200 light:text-zinc-800 transition hover:border-white/35 light:hover:border-black/30 hover:text-zinc-50 light:hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 light:focus-visible:ring-zinc-700 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 sm:h-8 sm:gap-1.5 sm:border-white/[0.12] sm:px-3 sm:text-xs sm:text-zinc-300"
    >
      <MapIcon className="h-5 w-5 text-pulse sm:h-4 sm:w-4" aria-hidden />
      {t("MAP_OPEN")}
    </button>
  );

  // Collapsed: no section header, no skeletons — one quiet row explaining why
  // the strip is missing, with the actions that still make sense.
  if (!pending && !hasResults) {
    // Say the ACTUAL reason (the hook's `reason` distinguishes timeout /
    // unsupported / insecure-context behind the one "unavailable" status),
    // and offer a retry only where retrying can change the outcome.
    const hint =
      geo.status === "denied"
        ? t("NEARBY_DENIED")
        : geo.status === "unavailable"
          ? geo.reason === "timeout"
            ? t("NEARBY_TIMEOUT")
            : geo.reason === "unsupported"
              ? t("NEARBY_UNSUPPORTED")
              : geo.reason === "insecure_context"
                ? t("NEARBY_INSECURE")
                : t("NEARBY_UNAVAILABLE")
          : t("NEARBY_NONE");
    const canRetry =
      geo.status === "denied" ||
      (geo.status === "unavailable" &&
        geo.reason !== "unsupported" &&
        geo.reason !== "insecure_context");
    return (
      <section className="mt-6 sm:mt-8" aria-label={t("NEARBY_TITLE")}>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-2xl border border-white/[0.12] light:border-black/10 bg-zinc-900/40 light:bg-white/60 px-4 py-3.5">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 text-sm text-zinc-400 light:text-zinc-600">
              <Navigation className="h-4 w-4 shrink-0 text-zinc-600" aria-hidden />
              {hint}
            </p>
            {geo.status === "denied" && (
              // A cached denial re-errors instantly with no prompt — tell the
              // user the fix lives in the browser's site settings.
              <p className="mt-1 text-xs leading-relaxed text-dim light:text-zinc-600">{t("NEARBY_DENIED_HINT")}</p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            {canRetry && (
              <button
                type="button"
                onClick={geo.request}
                className="inline-flex h-12 shrink-0 items-center gap-1.5 rounded-full bg-paper light:bg-ink px-5 text-[15px] font-semibold text-zinc-900 light:text-zinc-50 shadow-[0_2px_10px_-2px_rgba(245,241,232,0.35)] transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 light:focus-visible:ring-zinc-700 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 sm:h-8 sm:px-3.5 sm:text-xs"
              >
                <Navigation className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden />
                {t(geo.status === "denied" ? "NEARBY_RETRY" : "RETRY")}
              </button>
            )}
            {mapPill}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-6 sm:mt-8" aria-label={t("NEARBY_TITLE")}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="inline-flex items-center gap-2 font-display text-lg tracking-tight text-zinc-100 light:text-zinc-900 sm:text-xl">
          <Navigation className="h-4 w-4 text-pulse" aria-hidden />
          {t("NEARBY_TITLE")}
        </h2>
        <div className="flex items-center gap-1.5">
          {mapPill}
          {hasResults && overflowing && (
            <div className="hidden items-center gap-1.5 sm:flex">
              <ArrowBtn dir="left" onClick={() => scrollBy(-1)} />
              <ArrowBtn dir="right" onClick={() => scrollBy(1)} />
            </div>
          )}
        </div>
      </div>

      {/* States ----------------------------------------------------------- */}
      {pending ? (
        <div className="relative mt-3">
          <div className={TRACK} aria-hidden>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={CARD_WRAP}>
                <div className="h-72 w-full animate-pulse rounded-2xl border border-white/10 light:border-black/10 bg-zinc-900/50 light:bg-white/60" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="relative mt-3">
          {/* edge fade masks (desktop polish; mobile bleeds to the screen edge) */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-8 bg-gradient-to-r from-background to-transparent sm:block" aria-hidden />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-8 bg-gradient-to-l from-background to-transparent sm:block" aria-hidden />
          <div ref={scroller} className={TRACK}>
            {nearby.map(({ r, km }) => (
              <div key={r.id} className={CARD_WRAP}>
                <RestaurantCard
                  restaurant={r}
                  isOpen
                  discounts={discountsByRestaurant[r.id]}
                  onSelect={onSelect}
                  distanceLabel={formatDistance(km, lang)}
                  className="h-full"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
